import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { 
    Calculator, Euro, Building2, User, Users, AlertTriangle, 
    TrendingUp, PieChart, Eye, EyeOff, Lock
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

/**
 * CommissionSimulator - Simulateur visuel des commissions V2
 * 
 * RÈGLES DE VISIBILITÉ:
 * =====================
 * - Admin Full/Admin: Voir TOUT le breakdown
 * - VP: Voir SA commission brute/nette + ce qu'il paye à l'apporteur
 * - Apporteur: Voir UNIQUEMENT sa commission
 */

export function CommissionSimulator({ 
    teamConfig = {},  // { vpp_pct, vps_pct, threshold_pct }
    apporteurPct = 50,
    vppUserId = null,
    vpsUserId = null,
    defaultCommission = 1000,
    compact = false
}) {
    const { user } = useAuth();
    const [commissionTotale, setCommissionTotale] = useState(defaultCommission);
    
    // Rôles et permissions de visibilité
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    const canSeeAll = isAdminFull || isAdmin;
    
    // Déterminer si l'utilisateur est VPP ou VPS de cette équipe
    const isVPP = isVP && user?.id === vppUserId;
    const isVPS = isVP && user?.id === vpsUserId;
    
    // Paramètres de l'équipe
    const vppPct = teamConfig.vpp_pct ?? 25;
    const vpsPct = teamConfig.vps_pct ?? 10;
    const thresholdPct = teamConfig.threshold_pct ?? 30;
    const teamPct = vppPct + vpsPct;
    
    // ============== CALCUL V2 ==============
    const calculate = useCallback(() => {
        const cg = parseFloat(commissionTotale) || 0;
        
        // Étape 1-2: Parts VP directement sur commission totale
        const vppGross = cg * (vppPct / 100);
        const vpsGross = cg * (vpsPct / 100);
        
        // Étape 3: Commission équipe = somme des parts VP
        const teamCommission = vppGross + vpsGross;
        
        // Étape 4: Commission apporteur (sur VPP brut)
        const apporteurCommission = vppGross * (apporteurPct / 100);
        
        // Étape 5: VPP net
        const vppNet = vppGross - apporteurCommission;
        
        // Étape 6: Reste après équipe
        const rest = cg - teamCommission;
        
        // Étape 7-8: Split 50/50 sur le reste
        const valnexoBase = rest * 0.50;
        const atService = rest * 0.50;
        
        // Étape 9: Règle du seuil (protection AT Service)
        const thresholdExceeded = teamPct > thresholdPct;
        let excessAmount = 0;
        let valnexo = valnexoBase;
        
        if (thresholdExceeded) {
            const excessPct = teamPct - thresholdPct;
            excessAmount = cg * (excessPct / 100);
            valnexo = valnexoBase - excessAmount;
        }
        
        return {
            commissionTotale: cg,
            vppGross: Math.round(vppGross * 100) / 100,
            vpsGross: Math.round(vpsGross * 100) / 100,
            teamCommission: Math.round(teamCommission * 100) / 100,
            apporteurCommission: Math.round(apporteurCommission * 100) / 100,
            vppNet: Math.round(vppNet * 100) / 100,
            rest: Math.round(rest * 100) / 100,
            valnexoBase: Math.round(valnexoBase * 100) / 100,
            valnexo: Math.round(valnexo * 100) / 100,
            atService: Math.round(atService * 100) / 100,
            thresholdExceeded,
            excessAmount: Math.round(excessAmount * 100) / 100
        };
    }, [commissionTotale, vppPct, vpsPct, apporteurPct, teamPct, thresholdPct]);
    
    const result = calculate();
    
    // Composant pour afficher une ligne avec visibilité conditionnelle
    const CommissionRow = ({ 
        label, 
        amount, 
        percent, 
        color = 'slate', 
        icon: Icon,
        visible = true,
        highlight = false,
        negative = false
    }) => {
        if (!visible) {
            return (
                <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 opacity-50">
                    <div className="flex items-center gap-2">
                        <EyeOff className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-400">{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Lock className="w-3 h-3 text-slate-400" />
                        <span className="text-slate-400">***</span>
                    </div>
                </div>
            );
        }
        
        const colorClasses = {
            slate: 'text-slate-700',
            emerald: 'text-emerald-700 bg-emerald-50',
            blue: 'text-blue-700 bg-blue-50',
            amber: 'text-amber-700 bg-amber-50',
            purple: 'text-purple-700 bg-purple-50',
            red: 'text-red-600 bg-red-50'
        };
        
        return (
            <div className={cn(
                "flex items-center justify-between py-2 px-3 rounded-lg transition-all",
                highlight && colorClasses[color],
                !highlight && "hover:bg-slate-50"
            )}>
                <div className="flex items-center gap-2">
                    {Icon && <Icon className={cn("w-4 h-4", highlight ? colorClasses[color].split(' ')[0] : 'text-slate-500')} />}
                    <span className="text-sm">{label}</span>
                    {percent !== undefined && (
                        <Badge variant="outline" className="text-xs">
                            {percent}%
                        </Badge>
                    )}
                </div>
                <span className={cn(
                    "font-semibold tabular-nums",
                    negative && "text-red-600"
                )}>
                    {negative && '-'}{Math.abs(amount).toLocaleString('fr-FR')} €
                </span>
            </div>
        );
    };
    
    // Barre de répartition visuelle
    const DistributionBar = () => {
        const total = result.commissionTotale || 1;
        const segments = [
            { label: 'Valnexo', value: result.valnexo, color: 'bg-slate-500', visible: canSeeAll },
            { label: 'AT Service', value: result.atService, color: 'bg-slate-400', visible: canSeeAll },
            { label: 'VPP Net', value: result.vppNet, color: 'bg-emerald-500', visible: canSeeAll || isVPP },
            { label: 'VPS', value: result.vpsGross, color: 'bg-blue-500', visible: canSeeAll || isVPS },
            { label: 'Apporteur', value: result.apporteurCommission, color: 'bg-purple-500', visible: canSeeAll || isVPP || isApporteur },
        ].filter(s => s.visible && s.value > 0);
        
        return (
            <div className="space-y-2">
                <div className="h-6 rounded-full overflow-hidden flex bg-slate-200">
                    {segments.map((seg, i) => (
                        <div
                            key={i}
                            className={cn("h-full transition-all", seg.color)}
                            style={{ width: `${(seg.value / total) * 100}%` }}
                            title={`${seg.label}: ${seg.value.toLocaleString('fr-FR')} €`}
                        />
                    ))}
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                    {segments.map((seg, i) => (
                        <div key={i} className="flex items-center gap-1">
                            <div className={cn("w-3 h-3 rounded", seg.color)} />
                            <span className="text-slate-600">{seg.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    
    if (compact) {
        // Version compacte pour intégration dans d'autres composants
        return (
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Simulation
                    </span>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            value={commissionTotale}
                            onChange={(e) => setCommissionTotale(e.target.value)}
                            className="w-24 h-8 text-sm"
                        />
                        <span className="text-sm text-slate-500">€</span>
                    </div>
                </div>
                <DistributionBar />
                {/* Affichage selon visibilité */}
                {canSeeAll && (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>Valnexo: <strong>{result.valnexo.toLocaleString('fr-FR')} €</strong></div>
                        <div>AT Service: <strong>{result.atService.toLocaleString('fr-FR')} €</strong></div>
                        <div>VPP Net: <strong>{result.vppNet.toLocaleString('fr-FR')} €</strong></div>
                        <div>VPS: <strong>{result.vpsGross.toLocaleString('fr-FR')} €</strong></div>
                        <div className="col-span-2">Apporteur: <strong>{result.apporteurCommission.toLocaleString('fr-FR')} €</strong></div>
                    </div>
                )}
                {isVPP && !canSeeAll && (
                    <div className="text-xs">
                        <div>Ma commission brute: <strong>{result.vppGross.toLocaleString('fr-FR')} €</strong></div>
                        <div>Je paye à l'apporteur: <strong className="text-red-600">-{result.apporteurCommission.toLocaleString('fr-FR')} €</strong></div>
                        <div>Ma commission nette: <strong className="text-emerald-600">{result.vppNet.toLocaleString('fr-FR')} €</strong></div>
                    </div>
                )}
                {isVPS && !canSeeAll && !isVPP && (
                    <div className="text-xs">
                        <div>Ma commission: <strong className="text-blue-600">{result.vpsGross.toLocaleString('fr-FR')} €</strong></div>
                    </div>
                )}
                {isApporteur && !canSeeAll && (
                    <div className="text-xs">
                        <div>Ma commission: <strong className="text-purple-600">{result.apporteurCommission.toLocaleString('fr-FR')} €</strong></div>
                    </div>
                )}
            </div>
        );
    }
    
    return (
        <Card className="border-t-4 border-t-indigo-500" data-testid="commission-simulator">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-indigo-600" />
                    Simulateur de Commission
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Visibilité: {canSeeAll ? 'Complète (Admin)' : isVPP ? 'VP Payeur' : isVPS ? 'VP Superviseur' : isApporteur ? 'Apporteur' : 'Limitée'}
                </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
                {/* Input Commission Totale */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-indigo-500" />
                        Commission Totale HT
                    </Label>
                    <div className="flex items-center gap-3">
                        <Input
                            type="number"
                            min="0"
                            step="100"
                            value={commissionTotale}
                            onChange={(e) => setCommissionTotale(e.target.value)}
                            className="max-w-xs"
                            data-testid="simulator-commission-input"
                        />
                        <span className="text-slate-500">€</span>
                    </div>
                </div>
                
                {/* Paramètres utilisés */}
                {canSeeAll && (
                    <div className="p-3 bg-indigo-50 rounded-lg space-y-2">
                        <h4 className="font-medium text-sm text-indigo-800">Paramètres équipe</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div>
                                <span className="text-indigo-600">VPP:</span>
                                <span className="font-semibold ml-1">{vppPct}%</span>
                            </div>
                            <div>
                                <span className="text-indigo-600">VPS:</span>
                                <span className="font-semibold ml-1">{vpsPct}%</span>
                            </div>
                            <div>
                                <span className="text-indigo-600">Équipe:</span>
                                <span className="font-semibold ml-1">{teamPct}%</span>
                            </div>
                            <div>
                                <span className="text-indigo-600">Seuil:</span>
                                <span className="font-semibold ml-1">{thresholdPct}%</span>
                            </div>
                        </div>
                        {apporteurPct > 0 && (
                            <div className="text-xs">
                                <span className="text-indigo-600">Apporteur (% sur VPP):</span>
                                <span className="font-semibold ml-1">{apporteurPct}%</span>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Avertissement seuil dépassé */}
                {result.thresholdExceeded && canSeeAll && (
                    <div className="flex items-center gap-2 p-3 bg-amber-100 rounded-lg text-amber-800 text-sm">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                        <div>
                            <strong>Seuil dépassé !</strong> Part équipe ({teamPct}%) &gt; Seuil ({thresholdPct}%).
                            <br />
                            <span className="text-xs">Excès de {result.excessAmount.toLocaleString('fr-FR')} € déduit de Valnexo (AT Service protégé).</span>
                        </div>
                    </div>
                )}
                
                {/* Barre de répartition visuelle */}
                <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Répartition visuelle</Label>
                    <DistributionBar />
                </div>
                
                <Separator />
                
                {/* Breakdown détaillé selon visibilité */}
                <div className="space-y-1">
                    <Label className="text-sm text-slate-600 mb-2 block">Détail des commissions</Label>
                    
                    {/* Valnexo & AT Service - Admin only */}
                    <CommissionRow 
                        label="Valnexo" 
                        amount={result.valnexo} 
                        icon={Building2}
                        visible={canSeeAll}
                    />
                    <CommissionRow 
                        label="AT Service" 
                        amount={result.atService} 
                        icon={Building2}
                        visible={canSeeAll}
                    />
                    
                    {canSeeAll && <Separator className="my-2" />}
                    
                    {/* VP Payeur - Admin + VPP */}
                    <CommissionRow 
                        label="VP Payeur (brut)" 
                        amount={result.vppGross}
                        percent={vppPct}
                        icon={User}
                        visible={canSeeAll || isVPP}
                        color="emerald"
                    />
                    
                    {/* Ce que VPP paye à l'apporteur - Admin + VPP */}
                    {result.apporteurCommission > 0 && (
                        <CommissionRow 
                            label="└ Payé à l'apporteur" 
                            amount={result.apporteurCommission}
                            icon={Users}
                            visible={canSeeAll || isVPP}
                            negative
                        />
                    )}
                    
                    <CommissionRow 
                        label="VP Payeur (net)" 
                        amount={result.vppNet}
                        icon={TrendingUp}
                        visible={canSeeAll || isVPP}
                        highlight
                        color="emerald"
                    />
                    
                    {(canSeeAll || isVPP) && <Separator className="my-2" />}
                    
                    {/* VP Superviseur - Admin + VPS */}
                    <CommissionRow 
                        label="VP Superviseur" 
                        amount={result.vpsGross}
                        percent={vpsPct}
                        icon={User}
                        visible={canSeeAll || isVPS}
                        highlight
                        color="blue"
                    />
                    
                    {(canSeeAll || isVPS) && result.vpsGross > 0 && <Separator className="my-2" />}
                    
                    {/* Apporteur - Admin + VPP + Apporteur */}
                    <CommissionRow 
                        label="Apporteur" 
                        amount={result.apporteurCommission}
                        percent={apporteurPct}
                        icon={Users}
                        visible={canSeeAll || isVPP || isApporteur}
                        highlight
                        color="purple"
                    />
                </div>
                
                {/* Total distribué - Admin only */}
                {canSeeAll && (
                    <>
                        <Separator />
                        <div className="flex items-center justify-between py-2 px-3 bg-slate-100 rounded-lg">
                            <span className="font-semibold">Total distribué</span>
                            <span className="font-bold text-lg">
                                {(result.valnexo + result.atService + result.vppNet + result.vpsGross + result.apporteurCommission).toLocaleString('fr-FR')} €
                            </span>
                        </div>
                    </>
                )}
                
                {/* Message pour utilisateurs avec visibilité limitée */}
                {!canSeeAll && (
                    <div className="p-3 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        <span>Certaines informations sont masquées selon votre niveau d'accès.</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default CommissionSimulator;
